import { TranslationKey } from './translation/TranslationKey';

import { cloneDeep } from '@apollo/client/utilities';
import { CompanyEntity, forCreate, forDelete, forUpdate } from './common/entity';
import { ProductMasterData } from './product_master_data/productMasterData';
import { StockLocation } from './stock_location/stockLocation';
import { Lot } from './lot/lot';
import { CustomField } from './custom_field/customField';
import { StockLocationAggregateType } from './common/stockLocationAggregateType';
import { t } from './translation/Translator';
import { User } from './user/user';
import { Tag } from './tag';

export const DefaultTriggerIds = {
  lot: '00000000-0000-0000-0000-000000000001',
  task: '00000000-0000-0000-0000-000000000002',
} as const;

export enum TriggerType {
  alert = 'alert',
  email = 'email',
  pushNotification = 'pushNotification',
}

export enum TriggerEntityType {
  pmd = 'pmd',
  lot = 'lot',
  productTransaction = 'productTransaction',
  task = 'task',
  order = 'order',
  product = 'product',
  customField = 'customField',
}

export enum TriggerEntityParentType {
  order = 'order',
}

export enum ProductRefenceTriggerType {
  quantity = 'quantity',
}

export enum ProductReferenceQuantityTriggerValueType {
  reorderPoint = 'reorderPoint',
  value = 'value',
}

export enum LotTriggerType {
  expiry = 'expiry',
}

export enum ProductTransactionTriggerType {
  updatedField = 'updatedField',
  dueDate = 'dueDate',
}

export enum TaskTriggerType {
  dueDate = 'dueDate',
}

export class Trigger extends CompanyEntity {
  types: TriggerType[] = [TriggerType.alert];
  entityType!: TriggerEntityType;
  entityParentType?: TriggerEntityParentType;
  entityValue!: string;
  entityIds: string[] = [];
  stockLocationAggregateType: StockLocationAggregateType = StockLocationAggregateType.all;
  stockLocationIds: string[] = [];
  userIds: string[] = [];
  customFieldIds: string[] = [];
  notifyEntityAssignees: boolean = false;
  enabled: boolean = true;

  constructor(obj: any) {
    if (!obj.companyId) return;
    super(obj.companyId);
    Object.assign(this, cloneDeep(obj));
  }

  withTypes(types: TriggerType[]) {
    this.types = types;
    return cloneDeep(this);
  }

  withEntityType(type: TriggerEntityType) {
    if (type === this.entityType) return cloneDeep(this);

    this.entityIds = [];
    this.customFieldIds = [];
    this.stockLocationIds = [];

    switch (type) {
      case TriggerEntityType.productTransaction:
        this.entityParentType = TriggerEntityParentType.order;
        this.entityValue = '${DUE_DATE}0';
        break;
      case TriggerEntityType.pmd:
      case TriggerEntityType.lot:
        this.entityValue = '${EXPIRY_DATE}0';
        break;
      case TriggerEntityType.task:
        this.entityValue = '${DUE_DATE}0';
        break;
      case TriggerEntityType.customField:
        this.stockLocationAggregateType = StockLocationAggregateType.any;
        this.entityValue = '${DATE_TRIGGER_VALUE}0';
        break;
      default:
        this.entityParentType = undefined;
        break;
    }
    this.entityType = type;
    return cloneDeep(this);
  }

  withEntityValue(value: string) {
    this.entityValue = value;
    return cloneDeep(this);
  }

  withEntityIds(ids: string[]) {
    this.entityIds = ids;
    return cloneDeep(this);
  }

  withStockLocationIds(ids: string[]) {
    this.stockLocationIds = ids;
    return cloneDeep(this);
  }

  withStockLocationAggregate(value: StockLocationAggregateType) {
    this.stockLocationAggregateType = value;
    return cloneDeep(this);
  }

  withUserIds(userIds: string[]) {
    this.userIds = userIds;
    return cloneDeep(this);
  }

  // Only 1 custom field for now
  withCustomFieldId(customFieldId: string) {
    if (this.customFieldIds.length) this.customFieldIds[0] = customFieldId;
    else this.customFieldIds.push(customFieldId);
    return cloneDeep(this);
  }

  withNotifyEntityAssignees(value: boolean) {
    this.notifyEntityAssignees = value;
    return cloneDeep(this);
  }

  forUpdate() {
    return UpdateTriggerInput.from(this, UpdateTriggerInput);
  }

  forDelete(): DeleteTriggerInput {
    return DeleteTriggerInput.from(this, DeleteTriggerInput);
  }

  validate(fields: any[]) {
    throw new Error('Method not implemented.');
  }

  withEnabled(value: boolean) {
    this.enabled = value;
    return cloneDeep(this);
  }
}

export class CreateTriggerInput extends forCreate(Trigger) {}

export class UpdateTriggerInput extends forUpdate(Trigger) {}

export class DeleteTriggerInput extends forDelete(Trigger) {}

export function validStockLocationAggregateTypesForTriggerEntityType(
  entityType: TriggerEntityType,
): StockLocationAggregateType[] {
  switch (entityType) {
    case TriggerEntityType.lot:
      return [];
    case TriggerEntityType.pmd:
      return [
        StockLocationAggregateType.all,
        StockLocationAggregateType.allOf,
        StockLocationAggregateType.anyOf,
        StockLocationAggregateType.any,
      ];
    case TriggerEntityType.productTransaction:
      return [StockLocationAggregateType.any, StockLocationAggregateType.anyOf];
    case TriggerEntityType.task:
      return [StockLocationAggregateType.any, StockLocationAggregateType.anyOf];
    case TriggerEntityType.order:
      return [
        StockLocationAggregateType.all,
        StockLocationAggregateType.allOf,
        StockLocationAggregateType.anyOf,
        StockLocationAggregateType.any,
      ];
    case TriggerEntityType.product:
      return [
        StockLocationAggregateType.all,
        StockLocationAggregateType.allOf,
        StockLocationAggregateType.anyOf,
        StockLocationAggregateType.any,
      ];
    case TriggerEntityType.customField:
      return [
        StockLocationAggregateType.all,
        StockLocationAggregateType.allOf,
        StockLocationAggregateType.anyOf,
        StockLocationAggregateType.any,
      ];
  }
}

export function triggerTypeToString(type: TriggerType): string {
  switch (type) {
    case TriggerType.alert:
      return t(TranslationKey.alert).singular.label;
    case TriggerType.email:
      return t(TranslationKey.email).singular.label;
    case TriggerType.pushNotification:
      return t(TranslationKey.pushNotification).singular.label;
  }
}

export function triggerEntityTypeToString(type: TriggerEntityType) {
  switch (type) {
    case TriggerEntityType.pmd:
      return `${t(TranslationKey.referenceData).singular.label} (${t(TranslationKey.expiry).singular.label})`;
    case TriggerEntityType.lot:
      return t(TranslationKey.lotNumber).singular.label;
    case TriggerEntityType.productTransaction:
      return t(TranslationKey.transactions).singular.label;
    case TriggerEntityType.task:
      return t(TranslationKey.task).plural.label;
    case TriggerEntityType.order:
      return t(TranslationKey.order).singular.label;
    case TriggerEntityType.product:
      return t(TranslationKey.product).singular.label;
    case TriggerEntityType.customField:
      return t(TranslationKey.customFields).singular.label;
  }
}

export function triggerEntityParentTypeToString(type: TriggerEntityParentType): string {
  switch (type) {
    case TriggerEntityParentType.order:
      return t(TranslationKey.order).singular.label;
  }
}

export function productReferenceTriggerTypeToString(type: ProductRefenceTriggerType): string {
  switch (type) {
    case ProductRefenceTriggerType.quantity:
      return t(TranslationKey.quantity).singular.label;
  }
}

function getPmdExpiryText(trigger: Trigger, productMasterData: Map<string, ProductMasterData>, tags: Map<string, Tag>) {
  if (trigger.entityValue.includes('${EXPIRY_DATE}')) {
    if (trigger.entityValue === '${EXPIRY_DATE}') {
      return `when a lot ${getPmdText(trigger, productMasterData, tags)} expires`;
    } else {
      const suffix = trigger.entityValue.split('${EXPIRY_DATE}');
      if (suffix.length < 2) return '0';

      return `${suffix[1]} days before a lot ${getPmdText(trigger, productMasterData, tags)} expires`;
    }
  }
  return trigger.entityValue;
}

function getPmdText(trigger: Trigger, productMasterData: Map<string, ProductMasterData>, tags: Map<string, Tag>) {
  if (!trigger.entityIds.length) return;
  const length = trigger.entityIds.length;

  if (trigger.entityIds[0].startsWith('tag::')) {
    return `for any reference data with tag: ${trigger.entityIds
      .map(e => tags.get(e.split('tag::')[1])?.name)
      .join(', ')}`;
  } else if (length > 1) {
    return `for any of ${length} reference data`;
  } else {
    return `for ${productMasterData.get(trigger.entityIds[0])?.productName}`;
  }
}

export function getTextForTrigger(
  trigger: Trigger,
  productMasterData: Map<string, ProductMasterData>,
  stockLocations: Map<string, StockLocation>,
  lots: Map<string, Lot>,
  customFields: Map<string, CustomField>,
  users: Map<string, User>,
  tags: Map<string, Tag>,
): string {
  switch (trigger.entityType) {
    case TriggerEntityType.pmd:
      return `Trigger an ${getTypeText(trigger)} ${getPmdExpiryText(trigger, productMasterData, tags)}`;
    case TriggerEntityType.lot:
      return `Trigger an ${getTypeText(trigger)} ${getLotExpiryText(trigger, lots)}`;
    case TriggerEntityType.productTransaction:
      return `Trigger an ${getTypeText(trigger)} ${getProductTransactionText(
        trigger,
        customFields,
      )} ${getStockLocationText(trigger, stockLocations)}`;
    case TriggerEntityType.task:
      return `Trigger an ${getTypeText(trigger)} ${getTaskText(trigger)} ${getStockLocationText(
        trigger,
        stockLocations,
      )}`;
    case TriggerEntityType.order:
      if (trigger.entityValue === 'COMPLETE') {
        return `Trigger an ${getTypeText(trigger)} when order is completed ${getStockLocationText(
          trigger,
          stockLocations,
        )} for${getUserText(trigger, users)}`;
      } else if (trigger.entityValue === 'CREATE') {
        return `Trigger an ${getTypeText(trigger)} when order is created ${getStockLocationText(
          trigger,
          stockLocations,
        )} for${getUserText(trigger, users)}`;
      }
      return '';
    case TriggerEntityType.product:
      if (trigger.entityValue === 'INBOUND') {
        return `Trigger an ${getTypeText(trigger)} when product is inbounded ${getStockLocationText(
          trigger,
          stockLocations,
        )} for${getUserText(trigger, users)}`;
      } else if (trigger.entityValue === 'OUTBOUND') {
        return `Trigger an ${getTypeText(trigger)} when product is outbounded ${getStockLocationText(
          trigger,
          stockLocations,
        )} for${getUserText(trigger, users)}`;
      } else if (trigger.entityValue === 'MOVE') {
        return `Trigger an ${getTypeText(trigger)} when product is moved from/to ${getStockLocationText(
          trigger,
          stockLocations,
        )} for${getUserText(trigger, users)}`;
      }
      return '';
    case TriggerEntityType.customField:
      const cf = customFields.get(trigger.entityIds[0]);
      if (!trigger.entityValue || trigger.entityValue.includes('${UPDATED_FIELD}')) {
        return `Trigger an ${getTypeText(trigger)} when ${cf?.name} on ${cf?.entityType} changes ${getStockLocationText(
          trigger,
          stockLocations,
        )} for${getUserText(trigger, users)}`;
      } else if (trigger.entityValue.includes('${DATE_TRIGGER_VALUE}')) {
        const value = parseFloat(trigger.entityValue.split('${DATE_TRIGGER_VALUE}')[1]);
        const valueText = (() => {
          if (value === 0) return 'the day of';
          if (value === 1) return 'the day before';
          if (value === -1) return 'the day after';

          if (value > 1) return `${Math.abs(value)} days before`;
          else return `${Math.abs(value)} days after`;
        })();

        return `Trigger an ${getTypeText(trigger)} ${valueText} ${cf?.name}`;
      }

      return '';
  }
}

function getTypeText(trigger: Trigger) {
  if (trigger.types.length === 1) {
    return 'alert';
  }

  let text = 'alert';
  const triggerTypesWithoutAlert = trigger.types.filter(type => type !== TriggerType.alert);

  if (triggerTypesWithoutAlert.length === 1) {
    return (text = text.concat(' and ', triggerTypeToString(triggerTypesWithoutAlert[0]).toLowerCase()));
  }

  for (let i = 0; i < triggerTypesWithoutAlert.length - 1; i++) {
    text = text.concat(', ', triggerTypeToString(triggerTypesWithoutAlert[i]).toLowerCase());
  }

  text = text.concat(
    ' and ',
    triggerTypeToString(triggerTypesWithoutAlert[triggerTypesWithoutAlert.length - 1]).toLowerCase(),
  );

  return text;
}

function getStockLocationText(trigger: Trigger, stockLocations: Map<string, StockLocation>) {
  if (trigger.stockLocationAggregateType === StockLocationAggregateType.all) {
    return 'in all stock locations';
  } else if (trigger.stockLocationAggregateType === StockLocationAggregateType.allOf) {
    return `on a stock location wide level (for ${trigger.stockLocationIds.length} stock locations)`;
  } else if (trigger.stockLocationAggregateType === StockLocationAggregateType.any) {
    return `in any stock location`;
  } else if (trigger.stockLocationAggregateType === StockLocationAggregateType.anyOf) {
    if (trigger.stockLocationIds.length === 1) {
      return `in ${stockLocations.get(trigger.stockLocationIds[0])?.name || 'Unknown stock location'}`;
    }
    return `in any of the ${trigger.stockLocationIds.length} stock locations`;
  }
}

function getUserText(trigger: Trigger, users: Map<string, User>) {
  if (trigger.userIds.length === 2) {
    return ` ${users.get(trigger.userIds[0])?.email} and ${users.get(trigger.userIds[1])?.email}`;
  }
  return trigger.userIds.map(user => ` ${users.get(user)?.email}`);
}

function getLotText(trigger: Trigger, lots: Map<string, Lot>) {
  if (trigger.entityIds.length === 1) {
    return lots.get(trigger.entityIds[0])?.number;
  } else if (!trigger.entityIds.length) {
    return 'a lot';
  } else {
    return `one of ${trigger.entityIds.length} lots`;
  }
}

function getLotExpiryText(trigger: Trigger, lots: Map<string, Lot>) {
  if (trigger.entityValue.includes('${EXPIRY_DATE}')) {
    if (trigger.entityValue === '${EXPIRY_DATE}') {
      return `when ${getLotText(trigger, lots)} expires`;
    } else {
      const suffix = trigger.entityValue.split('${EXPIRY_DATE}');
      if (suffix.length < 2) return '0';

      return `${suffix[1]} days before ${getLotText(trigger, lots)} expires`;
    }
  }
  return trigger.entityValue;
}

function getProductTransactionText(trigger: Trigger, customFields: Map<string, CustomField>) {
  let customFieldName = customFields.get(trigger.customFieldIds[0])?.name || 'Unknown Field';

  if (trigger.entityValue.includes('${DUE_DATE}')) {
    if (trigger.entityValue === '${DUE_DATE}') {
      return `1 day before field "${customFieldName}" on Product Transactions`;
    } else {
      const suffix = trigger.entityValue.split('${DUE_DATE}');
      if (suffix.length < 2) return '0';

      return `${suffix[1]} days before "${customFieldName}"`;
    }
  } else if (trigger.entityValue.includes('${UPDATED_FIELD}')) {
    return `when field "${customFieldName}" on Product Transactions is updated`;
  }
}

function getTaskText(trigger: Trigger): string {
  if (trigger.entityValue.includes('${DUE_DATE}')) {
    if (trigger.entityValue === '${DUE_DATE}') {
      return `on the due date of a task`;
    } else {
      const suffix = trigger.entityValue.split('${DUE_DATE}');
      if (suffix.length < 2) return '0';

      return `${suffix[1]} days before the due date of a task`;
    }
  }

  return '';
}

export function productReferenceQuantityTriggerValueToCode(type: ProductReferenceQuantityTriggerValueType): string {
  switch (type) {
    case ProductReferenceQuantityTriggerValueType.reorderPoint:
      return '${REORDER_POINT}';
    case ProductReferenceQuantityTriggerValueType.value:
      return '${VALUE}';
  }
}

export function quantityTriggerValueTypeToString(type: ProductReferenceQuantityTriggerValueType): string {
  switch (type) {
    case ProductReferenceQuantityTriggerValueType.reorderPoint:
      return 'the reorder point';
    case ProductReferenceQuantityTriggerValueType.value:
      return 'a custom value';
  }
}

export function codeToProductReferenceQuantityTriggerValueType(type: string) {
  if (!type) return;
  if (type.includes('${REORDER_POINT}')) {
    return ProductReferenceQuantityTriggerValueType.reorderPoint;
  } else if (type.includes('${VALUE}')) {
    return ProductReferenceQuantityTriggerValueType.value;
  }
}

export function productTransactionTriggerTypeToCode(type: ProductTransactionTriggerType): string {
  switch (type) {
    case ProductTransactionTriggerType.dueDate:
      return '${DUE_DATE}';
    case ProductTransactionTriggerType.updatedField:
      return '${UPDATED_FIELD}';
  }
}

export function productTransactionTriggerTypeToString(type: ProductTransactionTriggerType): string {
  switch (type) {
    case ProductTransactionTriggerType.dueDate:
      return 'Due Date';
    case ProductTransactionTriggerType.updatedField:
      return 'Updated Field';
  }
}

export function codeToProductTransactionTriggerType(value: string) {
  if (!value) return;
  if (value.includes('${UPDATED_FIELD}')) {
    return ProductTransactionTriggerType.updatedField;
  } else if (value.includes('${DUE_DATE}')) {
    return ProductTransactionTriggerType.dueDate;
  }
}
